
import Vue from 'vue';
import TaxTable from './TaxTable.vue';
import Taxes from './Taxes.vue';
import BondInput from './BondInput.vue';
import HouseholdIncomeInput from './HouseholdIncomeInput.vue';
import HeaderCard from './HeaderCard.vue'
import SideBar from './SideBar.vue'
import { ClientConstants } from '../client.constants';
import TaxService from '../services/tax-service';
import CalculatorService from '../services/calculation-service';
import InfoWeCalculated from './InfoWeCalculated.vue';
import { CalculationResponseModel } from '../models/calculation-models';
import $ from 'jquery';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let app: any;

export default Vue.extend({
    name: 'MainCard',
    components: {
        BondInput,
        TaxTable,
        HouseholdIncomeInput,
        Taxes,
        HeaderCard,
        SideBar,
        InfoWeCalculated
    },
    computed: {
        orderedBondTypeColumnHeadings: function() {
            // default to CD column first when no bond type has been selected
            let currentBondType = this.inputModel.bondType == 'invalid' ? 'CD' : this.inputModel.bondType;
            let index = ClientConstants.BOND_OPTIONS.findIndex(bondType => bondType.value === currentBondType);

            let headings = [
                ClientConstants.CD_COLUMN_HEADING,
                ClientConstants.CORPORATE_COLUMN_HEADING,
                ClientConstants.IN_STATE_MUNICIPAL_COLUMN_HEADING,
                ClientConstants.OUT_OF_STATE_MUNICIPAL_COLUMN_HEADING,
                ClientConstants.TREASURY_AGENCY_COLUMN_HEADING,
                ClientConstants.SPDA_COLUMN_HEADING
            ];
            return headings.splice(index - 1,1).concat(headings);
        },
        orderedApplicableTaxesInfo: function() {
            // default to CD column first when no bond type has been selected
            let currentBondType = this.inputModel.bondType == 'invalid' ? 'CD' : this.inputModel.bondType;
            let index = ClientConstants.BOND_OPTIONS.findIndex(bondType => bondType.value === currentBondType);

            let cdTaxes = ClientConstants.CD_APPLICABLE_TAXES_INFO;
            let corpTaxes = ClientConstants.CORPORATE_APPLICABLE_TAXES_INFO;
            let inStateMuniTaxes = ['IA', 'IL', 'WI'].includes(this.inputModel.state)
                ? ClientConstants.OUT_OF_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO
                : ClientConstants.IN_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO;
            let outOfStateMuniTaxes = ['DC'].includes(this.inputModel.state)
                ? ClientConstants.IN_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO
                : ClientConstants.OUT_OF_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO;
            let treasuryAgencyTaxes = ClientConstants.TREASURY_AGENCY_APPLICABLE_TAXES_INFO;
            let spdaTaxes = ClientConstants.SPDA_APPLICABLE_TAXES_INFO;

            // default order
            let info = [cdTaxes, corpTaxes, inStateMuniTaxes, outOfStateMuniTaxes, treasuryAgencyTaxes, spdaTaxes];
            // reorder based on selected bond type
            info = info.splice(index - 1,1).concat(info);

            // since this is passed to the tax table component which expects an array of rows of data, wrap
            // the return value in an array so that this is the first (and only) row of data for the tax table
            return [info];
        }
    },
    data: () => {
        return {
            clientConstants: ClientConstants,

            federalTax: {},
            stateTax: {},

            inputModel: {
                bondType: ClientConstants.BOND_OPTIONS[0].value,
                bondYield: 0,
                filingStatus: ClientConstants.filingStatusList[0].value,
                annualIncome: '0',
                maxIncomeChecked: false,
                state: ClientConstants.stateOfResidenceList[0].value,
                localTax: 0,
            },
            yields: ClientConstants.EMPTY_YIELDS_TABLE,
            federalIsInvalid: false,
            stateIsInvalid: false,
            bondValueInvalid: false,
            spdaUrl: app ? `${app.spdaUrl}` : '',
        };
    },
    methods: {
        reset() {
            this.federalTax = {};
            this.stateTax = {};
            this.yields = ClientConstants.EMPTY_YIELDS_TABLE;
        },
        yieldValueChange(value: number) {
            this.inputModel.bondYield = value;
            this.reset();
        },
        bondValueChange(value: string) {
            this.bondValueInvalid = false;
            this.inputModel.bondType = value;
            this.reset();
        },
        annualIncomeChange(value: string) {
            this.inputModel.annualIncome = value;
            this.inputModel.maxIncomeChecked = false;
            this.reset();
        },
        maxIncomeChange(value: boolean) {
            this.inputModel.maxIncomeChecked = value;
            this.inputModel.annualIncome = value ? '' : '0';
            this.reset();
        },
        selectedFederalChange(value: any) {
            this.federalIsInvalid = false;
            this.inputModel.filingStatus = value;
            this.reset();
        },
        selectedStateChange(value: any) {
            this.stateIsInvalid = false;
            this.inputModel.state = value;
            this.reset();
        },
        localIncomeTaxChange(value: any) {
            this.inputModel.localTax = value;
            this.reset();
        },
        doValidation(form: any): boolean {
            // The call to checkValidity triggers the input fields to do validation based on native attributes.
            const formValid = form.checkValidity();
            this.bondValueInvalid = this.inputModel.bondType === this.clientConstants.BOND_OPTIONS[0].value;
            this.federalIsInvalid = this.inputModel.filingStatus === this.clientConstants.filingStatusList[0].value;
            this.stateIsInvalid = this.inputModel.state === this.clientConstants.stateOfResidenceList[0].value;
            return formValid && !this.bondValueInvalid && !this.federalIsInvalid && !this.stateIsInvalid;
        },
        processResults(yields: CalculationResponseModel) {
            let bondIndex = ClientConstants.BOND_OPTIONS.findIndex(bondType => bondType.value === this.inputModel.bondType);

            // Remove the requested bond type as well as the 'Select a Bond Type' options
            let bondTypeFields = ClientConstants.BOND_OPTIONS.filter(
                (bondType) =>
                    bondType.value != ClientConstants.BOND_OPTIONS[0].value &&
                    bondType.value !=
                        ClientConstants.BOND_OPTIONS[bondIndex].value
            );
            // Keep the requested bond type as the first column
            bondTypeFields.unshift(ClientConstants.BOND_OPTIONS[bondIndex]);

            this.yields =[]; // reset the yields value
            for (let row = 0; row < 21; row++) {
                this.yields.push(bondTypeFields.map(bondType => {
                    return (yields as any) [bondType.dataColumn] [row].toFixed(2) + '%';
                }));
            }
            let calculate: HTMLElement = this.$refs["calculate-button"] as HTMLElement;
            calculate.scrollIntoView();
            calculate.focus();
        },
        async onFormSubmit(event: any) {
            if (this.doValidation(this.getForm(event))) {
                try {
                    this.federalTax = this.inputModel.maxIncomeChecked
                        ? await TaxService.getMaxFederalTaxRate(this.inputModel.filingStatus)
                        : await TaxService.getFederalTaxRate(this.inputModel.filingStatus, Number(this.inputModel.annualIncome));
                    this.stateTax = this.inputModel.maxIncomeChecked
                        ? await TaxService.getMaxStateTaxRate(this.inputModel.state, this.inputModel.filingStatus)
                        : await TaxService.getStateTaxRate(this.inputModel.state, this.inputModel.filingStatus, Number(this.inputModel.annualIncome));

                    const yields = await CalculatorService.getYields(
                        this.inputModel.bondType, 
                        this.inputModel.bondYield, 
                        (this.federalTax as any).rate * 100,
                        this.inputModel.state,
                        (this.stateTax as any).rate * 100, 
                        this.inputModel.localTax);
                    this.processResults(yields);// process the response

                } catch(err: any) {
                    // TODO: display an error to the user - update unit tests accordingly
                    console.log(err.message);
                }
            } else {
                // If there was an invalid dropdown selection or input field value, move focus to the bond type field
                // so the user will see the error.
                const el = this.getBondTypeSelect();
                el.scrollIntoView();
                el.focus();
            }
        },
        getBondTypeSelect() {
            return $('#bond-type-input')[0];
        },
        // this function is here to allow us to mock the form when submit is triggered
        getForm(event: any) {
            return event.target;
        },
    },
});
